import { useEffect, useCallback, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { State } from 'reducers';
import {
    getBuildingByUrlSlug,
    getBuildingEvents,
    getBuildingEventsError,
    getBuildingHasMoreEvents,
    HasUrlSlug,
} from 'selectors/buildingsSelectors';
import { getEventLogRequest } from 'actions/buildingActions';
import { EventList } from './eventList';
import { makeCsvUrl, disposeCsvUrl } from 'ui/building/events/eventsExporter';
import { DeviceEvent } from 'shared/models';
import { values, Dictionary } from 'lodash';
import { useParams } from 'util/reactRouterHooks';


export const BuildingEventsPage = () => {
    const { t } = useTranslation();
    const params = useParams<HasUrlSlug>();

    const [ exportUrl, setExportUrl ] = useState<string>();

    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));
    const buildingId = building && building.id;

    const events = useSelector((state: State) => buildingId ? getBuildingEvents(state, { buildingId }) : null);
    const devices = building && building.devices;

    const deviceNameById = useMemo(() =>
        building && devices
            ? (id: string) => devices[id]?.displayName || id
            : (id: string) => id
    , [ building, devices ]);

    const hasMore = useSelector((state: State) => buildingId ? getBuildingHasMoreEvents(state, { buildingId }) : false);
    const errorMessage = useSelector((state: State) => buildingId ? getBuildingEventsError(state, { buildingId }) : null);

    const dispatch = useDispatch();

    const handleClickShowMore = useCallback(() => {
        if (!buildingId || !events) return;
        dispatch(getEventLogRequest({
            buildingId,
            next: getIdOfOldestEvent(events) - 1,
        }));
    }, [ dispatch, events, buildingId ]);

    useEffect(() => {
        if (buildingId) {
            dispatch(getEventLogRequest({ buildingId }));
        }
    }, [ dispatch, buildingId ]);

    useEffect(() => {
        let exportUrl: string | undefined;

        setExportUrl(() => {
            exportUrl = makeCsvUrl(events, deviceNameById);
            return exportUrl;
        });

        return () => disposeCsvUrl(exportUrl);
    }, [ setExportUrl, events, deviceNameById ]);
       
    return (
        <div
            id="building-events-page"
            className="page"
        >
            <a
                href={ exportUrl || '#' }
                className={ `ui basic button ${exportUrl ? '' : 'disabled'}` }
                style={ { marginBottom: '2em' } }
                download={ exportUrl && `${building && building.urlSlug}_events.csv` }
            >
                { t('COMMAND.EXPORT_CSV') }
            </a>
            <EventList
                events={ events }
                deviceNameById={ deviceNameById }
                onClickShowMore={ handleClickShowMore }
                hasMore={ hasMore }
                errorMessage={ errorMessage }
            />
        </div>
    );
};

function getIdOfOldestEvent(events: Dictionary<DeviceEvent>): number {
    return values(events).map(e => e.id).sort((a, b) => a! - b!).shift() || 0;
}
